import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Initialize variant-aware filtering for this gallery instance
    try {
      this.#initVariantFiltering();
    } catch (e) {
      // fail-safe: don't break gallery if filtering has an issue
      // console.debug('[media-gallery] variant filtering init failed', e);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    // cleanup if we created observers/listeners
    try {
      if (this._vfgMutationObserver) {
        this._vfgMutationObserver.disconnect();
        this._vfgMutationObserver = null;
      }
      if (this._vfgBoundEls) {
        (this._vfgBoundEls || []).forEach(el => {
          try {
            el.removeEventListener('change', el._vfgOnChange);
            el.removeEventListener('click', el._vfgOnClick);
          } catch (e) {}
        });
        this._vfgBoundEls = null;
      }
    } catch (err) {}
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  // ------------------- Variant-filter subsystem (scoped to this gallery) -------------------

  #vfgDebounceTimeout = 80;

  // initialize wiring: controls, observer and initial apply
  #initVariantFiltering() {
    // normalize helper
    const normalize = (s) =>
      (s || '')
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[_\u2011\-\s]+/g, ' ')
        .replace(/[^a-z0-9 ]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const debounce = (fn, wait = this.#vfgDebounceTimeout) => {
      let t;
      return (...a) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...a), wait);
      };
    };

    // local references
    const host = this; // gallery element
    const self = this;

    // read selected variant id (robust)
    function readSelectedVariantId() {
      // 1) checked radio with data-variant-id
      const checkedRadio =
        document.querySelector('input[type="radio"][data-variant-id]:checked, input[data-variant-id]:checked') ||
        document.querySelector('variant-picker input[type="radio"][checked], variant-picker input[type="radio"]:checked');
      if (checkedRadio) {
        if (checkedRadio.dataset && checkedRadio.dataset.variantId) return String(checkedRadio.dataset.variantId);
        if (checkedRadio.value && /^\d+$/.test(checkedRadio.value)) return String(checkedRadio.value);
      }

      // 2) variant-picker select option or select[name="id"] (Shopify select uses id values)
      const sel = document.querySelector('variant-picker select, select[name="id"], select[name="variant"]');
      if (sel) {
        if (sel.value && /^\d+$/.test(sel.value)) return String(sel.value);
        const opt = sel.options[sel.selectedIndex];
        if (opt && opt.dataset && opt.dataset.variantId) return String(opt.dataset.variantId);
      }

      // 3) variant buttons
      const activeBtn =
        document.querySelector('[data-variant-id].is-selected, [data-variant-id][aria-pressed="true"], [data-variant-id][aria-checked="true"]') ||
        document.querySelector('[data-variant-id].active');
      if (activeBtn && activeBtn.dataset && activeBtn.dataset.variantId) return String(activeBtn.dataset.variantId);

      // 4) URL param
      const params = new URLSearchParams(window.location.search);
      const variantParam = params.get('variant');
      if (variantParam) return String(variantParam);

      return null;
    }

    // read selected token (fallback - small subset of prior logic)
    function readSelectedToken() {
      // try radios / selects for textual token
      const radio = document.querySelector('variant-picker input[type="radio"][checked], variant-picker input[type="radio"]:checked, .swatch input:checked, input[data-option-value-id]:checked');
      if (radio) {
        if (radio.value) return radio.value;
        if (radio.getAttribute) return radio.getAttribute('aria-label') || radio.getAttribute('data-value') || radio.getAttribute('data-option-value') || null;
      }
      const sel = document.querySelector('variant-picker select, select[name="id"], select[name="variant"]');
      if (sel && sel.selectedIndex >= 0) {
        const opt = sel.options[sel.selectedIndex];
        if (opt) return opt.text || opt.value;
      }
      // SKU element fallback
      const skuEl = document.querySelector('[data-product-sku], .product-sku, .sku, .product__sku, [data-sku], .variant-sku');
      if (skuEl && skuEl.textContent) return skuEl.textContent.trim();
      return null;
    }

    // collect slides within this gallery (unique)
    function collectSlides() {
      const selectors = [
        '.product-media-container',
        '.slideshow__slide',
        '.splide__slide',
        '.slide',
        '.gallery-item',
        '.media-item',
        '.product-media',
      ];

      const items = [];
      const seen = new Set();

      selectors.forEach((sel) => {
        Array.from(host.querySelectorAll(sel)).forEach((node) => {
          if (!node) return;
          const mediaId = node.getAttribute && node.getAttribute('data-media-id');
          const key = mediaId || (node.querySelector('img') ? node.querySelector('img').src : node.outerHTML?.slice(0, 120));
          if (!key || seen.has(key)) return;
          seen.add(key);

          // read variant ids injected by Liquid
          const rawVariantIds = node.getAttribute && node.getAttribute('data-variant-ids');
          let variantIds = [];
          if (rawVariantIds && rawVariantIds.trim() !== '') {
            variantIds = rawVariantIds.split(',').map(t => t.trim()).filter(Boolean).map(String);
          }

          // alt text
          const dataMediaAltNode = node.querySelector('[data-media-alt]') || node;
          const dataAlt = dataMediaAltNode && dataMediaAltNode.getAttribute ? dataMediaAltNode.getAttribute('data-media-alt') : '';
          const img = node.querySelector('img');
          const imgAlt = img ? (img.getAttribute('alt') || '') : '';
          const rawAlt = (dataAlt || imgAlt || '').toString();

          items.push({ node, rawAlt, norm: normalize(rawAlt), variantIds });
        });
      });

      // fallback to images if nothing found
      if (items.length === 0) {
        Array.from(host.querySelectorAll('img')).forEach((img) => {
          const parent = img.closest('.product-media-container') || img.parentElement || img;
          const key = (img.src || '') + '::' + (parent.className || parent.outerHTML?.slice(0, 60));
          if (!key || seen.has(key)) return;
          seen.add(key);
          const rawAlt = img.getAttribute('alt') || '';
          items.push({ node: parent, rawAlt, norm: normalize(rawAlt), variantIds: [] });
        });
      }

      return items;
    }

    // show/hide logic + refresh
    function applyFilter() {
      const slides = collectSlides();
      if (!slides.length) return;

      // prefer explicit variant-id mapping
      const selectedVariantId = readSelectedVariantId();
      let visible = [];

      if (selectedVariantId) {
        slides.forEach((s) => {
          if (s.variantIds && s.variantIds.length > 0) {
            if (s.variantIds.includes(String(selectedVariantId))) visible.push(s);
          }
        });

        // fallback to token-based if none matched
        if (visible.length === 0) {
          const rawToken = readSelectedToken();
          const tokenNorm = normalize(rawToken || '');
          if (!tokenNorm) visible = slides.slice();
          else {
            slides.forEach((s) => {
              if (!s.norm) return;
              if (s.norm === tokenNorm || s.norm.includes(tokenNorm) || tokenNorm.includes(s.norm) || s.norm.split(' ').includes(tokenNorm)) visible.push(s);
            });
          }
        }
      } else {
        // no explicit variant id -> token/alt matching
        const rawToken = readSelectedToken();
        const tokenNorm = normalize(rawToken || '');
        if (!tokenNorm) visible = slides.slice();
        else {
          slides.forEach((s) => {
            if (!s.norm) return;
            if (s.norm === tokenNorm || s.norm.includes(tokenNorm) || tokenNorm.includes(s.norm) || s.norm.split(' ').includes(tokenNorm)) visible.push(s);
          });
        }
      }

      if (visible.length === 0) {
        visible = slides.slice();
      }

      // apply visibility
      slides.forEach((s) => {
        try {
          const show = visible.includes(s);
          s.node.style.display = show ? '' : 'none';
          if (show) s.node.removeAttribute('aria-hidden');
          else s.node.setAttribute('aria-hidden', 'true');
        } catch (err) {}
      });

      // attempt to refresh theme slideshow if available
      try {
        if (self.slideshow && typeof self.slideshow.refresh === 'function') {
          self.slideshow.refresh();
        } else if (self.slideshow && typeof self.slideshow.select === 'function') {
          // try resetting to first visible index if slideshow API lacks refresh
          const imageNodes = Array.from(host.querySelectorAll('img, .product-media-container'));
          const firstVisibleNode = visible.length ? visible[0].node : null;
          const firstVisibleIndex = firstVisibleNode ? imageNodes.indexOf(firstVisibleNode) : 0;
          if (typeof self.slideshow.select === 'function') self.slideshow.select(firstVisibleIndex, undefined, { animate: false });
        }
      } catch (e) {}
    }

    const debouncedApply = debounce(applyFilter, 80);

    // wire controls (best-effort across variants UI)
    function wireControls() {
      // elements to observe across the document (we wire at document-level because variant-picker can be outside the gallery)
      const selectors = [
        'variant-picker input',
        'variant-picker select',
        '.swatch input',
        '.swatch__item',
        '[data-swatch]',
        'input[data-option-value-id]',
        'input[data-variant-id]',
        'select[name="id"]',
      ];
      const els = Array.from(document.querySelectorAll(selectors.join(',')));
      self._vfgBoundEls = self._vfgBoundEls || [];

      els.forEach((el) => {
        if (el._vfgBound) return;
        const onChange = () => setTimeout(debouncedApply, 60);
        const onClick = () => setTimeout(debouncedApply, 60);
        el.addEventListener('change', onChange);
        el.addEventListener('click', onClick);
        el._vfgBound = true;
        el._vfgOnChange = onChange;
        el._vfgOnClick = onClick;
        self._vfgBoundEls.push(el);
      });

      // theme variant events
      ['variantupdate', 'VariantUpdateEvent', 'variant:change', 'product:variant:change', ThemeEvents.variantUpdate].forEach((evt) =>
        document.addEventListener(evt, () => setTimeout(debouncedApply, 60))
      );
    }

    // observe DOM changes within the gallery and variant picker region
    function observe() {
      const observeRoot = document.querySelector('variant-picker') || document.body;
      if (!observeRoot) return;
      const mo = new MutationObserver(
        debounce(() => {
          wireControls();
          debouncedApply();
        }, 140)
      );
      mo.observe(observeRoot, { childList: true, subtree: true, attributes: true });
      // store so we can disconnect later
      self._vfgMutationObserver = mo;
    }

    // initial run
    wireControls();
    debouncedApply();
    observe();
    // also run after a short delay to allow lazy widgets to initialize
    setTimeout(debouncedApply, 300);
    // refresh on visibility change (SPA)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') debouncedApply();
    });
  } // end #initVariantFiltering
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
